import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Database, Shield, Server,
  Network, Clock, BookOpen, User, Building2, FlaskConical, Activity, ToggleLeft,
  Package, ShieldCheck, EyeOff, Search, Lock, ClipboardList,
  Loader, CheckCircle, XCircle
} from 'lucide-react';
import LiveDemo from '../component/LiveDemo';
import PixelBlast from '../component/PixelBlast';
import './FaStrV2.css';

const TUTORIAL_STEPS = [
  { title: 'Connect to the Hospital Dashboard', description: 'On the Demo homepage, access the hospital dashboard and get an overview of the existing permissions' },
  { title: 'Connect as Patient', description: 'In the sidebar on the left, access the patient portal by entering a pateint id as patient_xxx (where xxx can be [001 - 200] inclusive). Change permissions in the patient portal to see real-time consent management' },
  { title: 'Check Hospital Dashboard', description: 'Verify that permission changes are reflected instantly on the hospital dashboard' },
  { title: 'Create New Agreement', description: "On the hospital dashboard, create a new Data Processing Agreement (DPA) by clicking the '+ Direct Proposal' button in the top right." },
  { title: 'Respond to Agreement', description: 'Accept or counter the agreement in the researcher dashboard' },
  { title: 'Request Dataset', description: 'Use the agreement in the researcher dashboard to make a dataset request' },
  { title: 'Accept Request', description: 'Approve the dataset request in the hospital dashboard' },
  { title: 'Wait for Proof Generation', description: 'Zero-knowledge proof generation takes approximately 5 minutes' },
  { title: 'View Compliance Dashboard', description: 'After 5 minutes, check the compliance dashboard to see cryptographic proof details' },
];

function DemoView({ demoInfo, onBack }) {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (!demoInfo?.expiresAt) return;
    const updateTimer = () => {
      const now = new Date().getTime();
      const expiresAt = new Date(demoInfo.expiresAt).getTime();
      const difference = expiresAt - now;
      if (difference <= 0) { setTimeRemaining('Expired'); return; }
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [demoInfo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTutorial && !event.target.closest('.tutorial-button-container')) setShowTutorial(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTutorial]);

  return (
    <div className="demo-view">
      <div className="demo-view-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
          Back to Project
        </button>
        <span className="demo-note">Note: Proof generation can take 5-6 minutes</span>
        <div className="tutorial-button-container">
          <button className="tutorial-button" onClick={() => setShowTutorial(!showTutorial)}>
            <BookOpen size={16} />
            Tutorial
          </button>
          {showTutorial && (
            <div className="tutorial-dropdown">
              <h3 className="tutorial-dropdown-title">Tutorial</h3>
              <div className="tutorial-dropdown-steps">
                {TUTORIAL_STEPS.map((step, idx) => (
                  <div key={idx} className="tutorial-dropdown-step">
                    <div className="tutorial-step-number">{idx + 1}</div>
                    <div className="tutorial-step-content">
                      <h4>{step.title}</h4>
                      <p>{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="demo-info-header">
          <span className="demo-title">FaSTr V2 Live Demo</span>
          {timeRemaining && (
            <span className="demo-timer">
              <Clock size={16} />
              {timeRemaining} remaining
            </span>
          )}
        </div>
      </div>
      <iframe
        src={`${demoInfo.proxyBase}?demoId=${demoInfo.demoId}`}
        className="demo-iframe"
        title="FaSTr V2 Live Demo"
      />
    </div>
  );
}

function FaStrV2() {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [demoInfo, setDemoInfo] = useState(null);
  const [demoStatus, setDemoStatus] = useState({ phase: 'idle' });

  useEffect(() => {
    if (showDemo) return;
    window.scrollTo(0, 0);

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.fade-in');
    animatedElements.forEach(el => observer.observe(el));

    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, [showDemo]);

  const handleDemoReady = (info) => {
    setDemoInfo(info);
    setShowDemo(true);
  };

  const handleBackToProject = () => setShowDemo(false);

  const demoStep1Status = demoStatus.phase === 'error'
    ? 'error'
    : demoStatus.phase === 'requesting-instance'
    ? 'active'
    : 'done';
  const demoStep2Status = demoStatus.phase === 'provisioning'
    ? 'active'
    : demoStatus.phase === 'ready'
    ? 'done'
    : 'pending';

  const demoStep1Label = demoStep1Status === 'active' || demoStep1Status === 'error'
    ? demoStatus.message
    : demoStep1Status === 'done' ? 'Spot instance acquired' : 'Requesting a spot instance from AWS';
  const demoStep2Label = demoStep2Status === 'active'
    ? demoStatus.message
    : demoStep2Status === 'done' ? 'App is live and reachable' : 'Waiting to provision the app';

  const demoStepIcon = (status) => {
    if (status === 'active') return <Loader size={16} className="spin" />;
    if (status === 'done') return <CheckCircle size={16} />;
    if (status === 'error') return <XCircle size={16} />;
    return null;
  };

  const demoOverallStatus = demoStatus.phase === 'ready'
    ? 'done'
    : demoStatus.phase === 'error'
    ? 'error'
    : 'active';

  const demoConnectorStatus = demoStep1Status === 'error'
    ? 'error'
    : demoStep2Status === 'done'
    ? 'done'
    : demoStep1Status === 'done'
    ? 'active'
    : 'pending';

  const flowNodes = [
    {
      icon: <Database size={28} />,
      title: "Hyperledger Fabric",
      subtitle: "Trust Layer",
      description: "Blockchain layer ensuring immutability and cryptographic integrity for all consent records",
      colorClass: "node-blue"
    },
    {
      icon: <Network size={28} />,
      title: "BRIDGE Protocol",
      subtitle: "Sync Layer",
      description: "Bidirectional reconciliation maintaining cryptographic trust while delegating to high-performance databases",
      colorClass: "node-amber"
    },
    {
      icon: <Server size={28} />,
      title: "Neo4j + PostgreSQL",
      subtitle: "Performance Layer",
      description: "Hierarchical consent trees in Neo4j with PostgreSQL lookup tables enabling sub-second queries",
      colorClass: "node-emerald"
    }
  ];

  const keyMetrics = [
    { value: "Upto 50×", label: "Performance Improvement", description: "Over pure blockchain approaches" },
    { value: "10M", label: "Scale Validated", description: "Synthetic dataset scale ceiling" },
    { value: "100%", label: "Cryptographic Integrity", description: "All operations verified on-chain" },
    { value: "GDPR", label: "Compliance", description: "Automated verification via ZK proofs" }
  ];

  const techStack = [
    { category: "Blockchain", techs: ["Hyperledger Fabric", "Go Chaincode"], colorClass: "tech-blue" },
    { category: "Databases", techs: ["Neo4j", "PostgreSQL"], colorClass: "tech-emerald" },
    { category: "Backend", techs: ["Node.js", "Python", "REST APIs"], colorClass: "tech-amber" },
    { category: "Frontend", techs: ["React", "Tailwind"], colorClass: "tech-teal" },
    { category: "Infrastructure", techs: ["Kubernetes", "AWS EC2", "Docker"], colorClass: "tech-indigo" },
    { category: "Security", techs: ["RISC0", "Zero-Knowledge Proofs", "Certificate Authorities"], colorClass: "tech-rose" }
  ];

  const stakeholders = [
    {
      Icon: User,
      role: 'Patient',
      desc: 'Owns their health data and gives their consent for it\'s use',
      colorClass: 'stakeholder-patient'
    },
    {
      Icon: Building2,
      role: 'Hospital',
      desc: 'Stores records, enforces consent, and must verify GDPR compliance',
      colorClass: 'stakeholder-hospital'
    },
    {
      Icon: FlaskConical,
      role: 'Clinical Researcher',
      desc: 'Needs cohorts of consenting patients to design and run studies',
      colorClass: 'stakeholder-researcher'
    },
  ];

  const swimlanePhases = [
    { name: 'Care', Icon: Activity },
    { name: 'Consent', Icon: ToggleLeft },
    { name: 'Proposal', Icon: ClipboardList },
    { name: 'Dataset', Icon: Package },
    { name: 'Compliance', Icon: ShieldCheck },
  ];

  const swimlaneActors = [
    {
      name: 'Patient',
      colorClass: 'actor-patient',
      cells: [
        { Icon: Activity, label: 'Receives treatment' },
        { Icon: ToggleLeft, label: 'Grants / revokes' },
        null,
        null,
        null,
      ]
    },
    {
      name: 'Hospital',
      colorClass: 'actor-hospital',
      cells: [
        { Icon: Database, label: 'EHR stored' },
        { Icon: ShieldCheck, label: 'Records consent' },
        { Icon: FileText, label: 'Reviews DPA' },
        { Icon: Server, label: 'Compiles cohort' },
        { Icon: ShieldCheck, label: 'Verifies proof' },
      ]
    },
    {
      name: 'Researcher',
      colorClass: 'actor-researcher',
      cells: [
        null,
        null,
        { Icon: ClipboardList, label: 'Submits DPA' },
        { Icon: Network, label: 'Requests dataset' },
        { Icon: Shield, label: 'Proves GDPR' },
      ]
    },
  ];

  const painPoints = [
    {
      actor: 'Patient',
      colorClass: 'pain-patient',
      Icon: EyeOff,
      headline: 'No real control over their data',
      desc: "Once consent is given, updating or revoking it is cumbersome and full of roadblocks — patients have nominal ownership but little practical control.",
    },
    {
      actor: 'Hospital',
      colorClass: 'pain-hospital',
      Icon: Lock,
      headline: 'Compliance is a black box',
      desc: "Consent records have no immutable trail. Verifying that a researcher processed data correctly relies on periodic manual audits — expensive, delayed, and easy to miss.",
    },
    {
      actor: 'Researcher',
      colorClass: 'pain-researcher',
      Icon: Search,
      headline: 'Slow cohorts, cumbersome proof',
      desc: 'Building a target patient cohort requires slow iterative queries. Proving the data was processed correctly means lengthy manual audits that expose proprietary methods.',
    },
  ];

  const solutionBridge = [
    {
      actor: 'Patient',
      actorClass: 'actor-patient',
      pain: 'No real control over their data',
      answer: 'Real-time consent management — grant, update, or revoke instantly on-chain',
    },
    {
      actor: 'Hospital',
      actorClass: 'actor-hospital',
      pain: 'Compliance is a black box',
      answer: 'Immutable on-chain consent records + automated ZK proof verification',
    },
    {
      actor: 'Researcher',
      actorClass: 'actor-researcher',
      pain: 'Slow cohorts, cumbersome proof',
      answer: 'Sub-second cohort queries + automatic ZK proof generation without revealing methods',
    },
  ];

  return (
    <div className="project-detail">
      <div className="page-background">
        <PixelBlast
          variant="square"
          pixelSize={10}
          color="#3a2828"
          patternScale={7}
          patternDensity={1.25}
          edgeFade={0.3}
          speed={0.3}
          transparent={true}
          enableRipples={true}
          rippleSpeed={0.4}
        />
      </div>
      {showDemo ? (
        <DemoView demoInfo={demoInfo} onBack={handleBackToProject} />
      ) : (
        <>
          {/* Hero Section */}
          <section className="project-hero">
            <div className="container">
              <button className="back-button" onClick={() => navigate('/')}>
                <ArrowLeft size={20} />
                Back to Portfolio
              </button>

              <div className="project-hero-content">
                <p className="project-overline">EURIS × UGA · FEATURED RESEARCH</p>
                <h1>FaSTr V2</h1>
                <hr className="hero-rule" />

                <div className="hero-two-col">
                  <div className="hero-left">
                    <p className="project-lede">
                      Fast, Secure and Transparent — Hybrid Blockchain Architecture for Healthcare
                    </p>
                    <p className="project-description">
                      A novel healthcare consent management system that solves the blockchain trust-performance
                      dilemma through intelligent hybrid architecture, achieving upto 50× performance improvements while
                      maintaining complete cryptographic integrity and GDPR compliance.
                    </p>
                    <div className="project-actions">
                      <LiveDemo onDemoReady={handleDemoReady} onStatusChange={setDemoStatus} />
                      <a
                        className="paper-button"
                        href="https://hal.science/hal-05730325v1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileText size={18} />
                        Read the Paper
                      </a>
                    </div>
                  </div>
                  <div className="hero-right">
                    <div className="research-snapshot">
                      <div className="snapshot-header">Research Snapshot</div>
                      <div className="snapshot-items">
                        <div className="snapshot-item">
                          <span className="snapshot-label">Venue</span>
                          <span className="snapshot-value">IEEE COMPSAC 2026</span>
                        </div>
                        <div className="snapshot-item">
                          <span className="snapshot-label">Scale</span>
                          <span className="snapshot-value">Up to 10M patients</span>
                        </div>
                        <div className="snapshot-item">
                          <span className="snapshot-label">Peak speedup</span>
                          <span className="snapshot-value snapshot-highlight">50×</span>
                        </div>
                        <div className="snapshot-item">
                          <span className="snapshot-label">Compliance</span>
                          <span className="snapshot-value">GDPR via ZK proofs</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Demo Status */}
          {demoStatus.phase !== 'idle' && (
            <div className={`demo-status-panel demo-status-panel--${demoOverallStatus}`}>
              <div className="demo-status-eyebrow">
                <span className="demo-status-eyebrow-label">
                  <span className="demo-status-eyebrow-dot" />
                  <span>
                    {demoStatus.phase === 'ready'
                      ? 'Your demo is ready'
                      : demoStatus.phase === 'error'
                      ? 'Something went wrong'
                      : 'Setting up your demo'}
                  </span>
                </span>
                {(demoStatus.phase === 'requesting-instance' || demoStatus.phase === 'provisioning') && (
                  <span className="demo-status-eta">
                    <Clock size={13} />
                    Est. 3 min
                  </span>
                )}
              </div>

              <div className="demo-status-pipeline">
                <div className={`demo-status-node demo-status-node--${demoStep1Status}`}>
                  <span className="demo-status-node-circle">{demoStepIcon(demoStep1Status)}</span>
                  <span className="demo-status-node-label">{demoStep1Label}</span>
                </div>
                <div className="demo-status-connector-track">
                  <span className={`demo-status-connector-fill demo-status-connector-fill--${demoConnectorStatus}`} />
                </div>
                <div className={`demo-status-node demo-status-node--${demoStep2Status}`}>
                  <span className="demo-status-node-circle">{demoStepIcon(demoStep2Status)}</span>
                  <span className="demo-status-node-label">{demoStep2Label}</span>
                </div>
              </div>

              {demoStatus.phase === 'ready' && (
                <p className="demo-status-hint">Click "Running Demo" above to open it.</p>
              )}
              {(demoStatus.phase === 'requesting-instance' || demoStatus.phase === 'provisioning') && (
                <p className="demo-status-hint">Feel free to explore the architecture below while you wait.</p>
              )}
            </div>
          )}

          {/* Metrics Strip */}
          <section className="metrics-section">
            <div className="container">
              <div className="metrics-strip">
                {keyMetrics.map((metric, idx) => (
                  <div key={idx} className="metric-item fade-in">
                    <span className="metric-category">{metric.label}</span>
                    <div className="metric-value">{metric.value}</div>
                    <p className="metric-description">{metric.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* The Stakeholders */}
          <section className="content-section stakeholders-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">01</div>
                <h2>The Stakeholders</h2>
              </div>
              <p className="section-intro fade-in">
                Three actors are involved in every EHR data sharing workflow.
              </p>
              <div className="stakeholder-cards fade-in">
                {stakeholders.map((s, idx) => (
                  <div key={idx} className={`stakeholder-card ${s.colorClass}`}>
                    <div className="stakeholder-icon"><s.Icon size={24} /></div>
                    <div className="stakeholder-role">{s.role}</div>
                    <p className="stakeholder-desc">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* The EHR Lifecycle */}
          <section className="content-section lifecycle-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">02</div>
                <h2>The EHR Lifecycle</h2>
              </div>
              <p className="section-intro fade-in">
                Every research dataset starts with a patient's care record and ends with a cryptographic compliance proof.
              </p>
              <div className="swimlane-wrapper fade-in">
                <div className="swimlane-grid">
                  {/* Header row */}
                  <div className="swimlane-corner" />
                  {swimlanePhases.map((phase, idx) => (
                    <div key={idx} className="swimlane-phase-header">
                      <phase.Icon size={13} />
                      <span>{phase.name}</span>
                    </div>
                  ))}

                  {/* Actor rows */}
                  {swimlaneActors.map((actor, aIdx) => (
                    <React.Fragment key={aIdx}>
                      <div className={`swimlane-actor-label ${actor.colorClass}`}>
                        {actor.name}
                      </div>
                      {actor.cells.map((cell, cIdx) => (
                        <div
                          key={cIdx}
                          className={`swimlane-cell ${actor.colorClass}${!cell ? ' swimlane-cell-empty' : ''}`}
                        >
                          {cell ? (
                            <>
                              <cell.Icon size={14} />
                              <span className="cell-label">{cell.label}</span>
                            </>
                          ) : (
                            <span className="cell-dash">—</span>
                          )}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <p className="lifecycle-caption fade-in">
                This is the workflow FaSTr V2 is designed to make fast, transparent, and cryptographically provable.
              </p>
            </div>
          </section>

          {/* Where the Current System Breaks */}
          <section className="content-section pain-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">03</div>
                <h2>Where the Current System Breaks</h2>
              </div>
              <div className="pain-cards fade-in">
                {painPoints.map((point, idx) => (
                  <div key={idx} className={`pain-card ${point.colorClass}`}>
                    <div className="pain-card-header">
                      <point.Icon size={18} />
                      <span className="pain-actor-label">{point.actor}</span>
                    </div>
                    <h3 className="pain-headline">{point.headline}</h3>
                    <p className="pain-desc">{point.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How FaSTr V2 Solves This */}
          <section className="content-section solution-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">04</div>
                <h2>How FaSTr V2 Solves This</h2>
              </div>
              <div className="solution-bridge fade-in">
                {solutionBridge.map((item, idx) => (
                  <div key={idx} className={`solution-row ${item.actorClass}`}>
                    <div className="solution-pain-col">
                      <span className="solution-actor-tag">{item.actor}</span>
                      <span className="solution-pain-text">{item.pain}</span>
                    </div>
                    <div className="solution-connector">
                      <span className="solution-arrow-line" />
                      <span className="solution-arrow-head">›</span>
                    </div>
                    <div className="solution-answer-col">
                      <span className="solution-answer-text">{item.answer}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Hybrid Architecture */}
          <section className="architecture-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">05</div>
                <h2>Hybrid Architecture</h2>
              </div>

              <p className="section-hook fade-in">Addresses — Hospital speed &amp; Patient integrity</p>

              <p className="section-intro fade-in">
                FaSTr V2 combines the strengths of blockchain and traditional databases through a novel
                three-tier architecture that maintains cryptographic integrity while enabling practical performance.
              </p>

              <div className="arch-flow fade-in">
                {flowNodes.map((node, idx) => (
                  <React.Fragment key={idx}>
                    <div className={`arch-node ${node.colorClass}`}>
                      <div className="arch-node-icon">{node.icon}</div>
                      <div className="arch-node-content">
                        <span className="arch-node-subtitle">{node.subtitle}</span>
                        <h3>{node.title}</h3>
                        <p>{node.description}</p>
                      </div>
                    </div>
                    {idx < flowNodes.length - 1 && (
                      <div className="arch-arrow">
                        <span className="arch-arrow-shaft" />
                        <span className="arch-arrow-head">›</span>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="arch-compliance-note fade-in">
                <Shield size={15} />
                <p><strong>RISC0 Zero-Knowledge Proofs</strong> integrate as a compliance verification layer — processors prove GDPR adherence without exposing patient data or proprietary algorithms.</p>
              </div>
            </div>
          </section>

          {/* BRIDGE Protocol */}
          <section className="content-section bridge-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">06</div>
                <h2>BRIDGE Protocol</h2>
              </div>

              <p className="section-hook fade-in">Addresses — Hospital: dataset consistency across millions of records</p>

              <div className="content-block fade-in">
                <h3>Bidirectional Reconciliation for Integrity-Driven ledGer-databasE sync</h3>
                <p>
                  The BRIDGE protocol is the key innovation that maintains cryptographic trust during delegation
                  between blockchain and high-performance databases. It operates in two complementary modes:
                </p>
              </div>

              <div className="bridge-dual-panel fade-in">
                <div className="bridge-panel bridge-panel-a">
                  <span className="bridge-letter-mark">A</span>
                  <div className="bridge-panel-content">
                    <span className="bridge-mode-label">MODE A — EVENT-DRIVEN</span>
                    <h4>Event-Driven Synchronization</h4>
                    <p>
                      Real-time updates without full database scans. When patients modify consent, hierarchical
                      trees are updated and hashes recomputed. The blockchain emits events that trigger immediate
                      synchronization to PostgreSQL lookup tables, ensuring sub-second propagation.
                    </p>
                  </div>
                </div>
                <div className="bridge-panel-divider" />
                <div className="bridge-panel bridge-panel-b">
                  <span className="bridge-letter-mark">B</span>
                  <div className="bridge-panel-content">
                    <span className="bridge-mode-label">MODE B — RECONCILIATION</span>
                    <h4>Reconciliation Mechanism</h4>
                    <p>
                      Fault-tolerant consistency verification using blockchain as the authoritative source of truth.
                      Before dataset compilation, the system compares "changed patients" lists between components.
                      Discrepancies are resolved by querying the blockchain, handling missed events, network failures,
                      or service restarts without data loss.
                    </p>
                  </div>
                </div>
              </div>

              <div className="content-block fade-in">
                <p>
                  This bidirectional approach ensures that even with 1% event loss affecting hundreds of patients,
                  reconciliation processes only the discrepancies rather than scanning millions of records.
                </p>
              </div>
            </div>
          </section>

          {/* Compliance */}
          <section className="content-section compliance-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">07</div>
                <h2>Automated GDPR Compliance</h2>
              </div>

              <p className="section-hook fade-in">Addresses — Hospital &amp; Researcher: manual compliance audits</p>

              <div className="content-block fade-in">
                <h3>Zero-Knowledge Proof Verification</h3>
                <p>
                  Beyond consent management, FaSTr V2 addresses GDPR compliance verification through automated
                  cryptographic proofs. Traditional compliance relies on manual audits spanning weeks or months.
                  Our system transforms this into real-time cryptographic assurance.
                </p>
              </div>

              <div className="compliance-steps fade-in">
                <div className="compliance-connector" />
                <div className="compliance-step">
                  <div className="compliance-step-number">01</div>
                  <h4>Machine-Readable DPA Terms</h4>
                  <p>
                    Data Processing Agreements encoded as smart contract terms on the blockchain, specifying
                    quantifiable requirements: k-anonymity thresholds, l-diversity guarantees, data minimization
                    constraints, temporal processing windows, and encryption requirements.
                  </p>
                </div>
                <div className="compliance-step">
                  <div className="compliance-step-number">02</div>
                  <h4>zkSTARK Proof System</h4>
                  <p>
                    Processors generate zero-knowledge proofs demonstrating GDPR compliance without exposing
                    patient data or proprietary algorithms. Using RISC0 zkVM, the system proves adherence to
                    DPA terms with sub-second verification times and post-quantum security guarantees.
                  </p>
                </div>
                <div className="compliance-step">
                  <div className="compliance-step-number">03</div>
                  <h4>Continuous Verification</h4>
                  <p>
                    Every data processing operation generates a cryptographic proof recorded on-chain. Automated
                    verification services validate proofs instantly, providing data controllers with real-time
                    compliance dashboards and eliminating months of manual audit overhead.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Performance Results */}
          <section className="results-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">08</div>
                <h2>Performance Results</h2>
              </div>

              <div className="perf-pull-stat fade-in">
                <div className="perf-pull-value">50×</div>
                <div className="perf-pull-label">peak speedup at 4M patients</div>
              </div>

              <div className="results-content fade-in">
                <div className="result-highlight">
                  <h3>Scalability Validation</h3>
                  <p>
                    Comprehensive evaluation with synthetic datasets ranging from 500,000 to 10,000,000 patients
                    demonstrates practical scalability. Single-category queries execute in under 15 seconds even
                    at the 10M patient scale, compared to 300+ seconds required by pure blockchain approaches.
                  </p>
                </div>

                <div className="comparison-table">
                  <h4>Query Performance at 4M Patients</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Query Type</th>
                        <th className="th-blockchain">Blockchain</th>
                        <th className="th-hybrid">Hybrid (FaSTr V2)</th>
                        <th className="th-speedup">Speedup</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="col-query">Single: Cardiology</td>
                        <td className="col-blockchain">139.5s</td>
                        <td className="col-hybrid">3.8s</td>
                        <td className="col-speedup bar-74">36.8×</td>
                      </tr>
                      <tr>
                        <td className="col-query">Single: Pulmonary</td>
                        <td className="col-blockchain">124.3s</td>
                        <td className="col-hybrid">3.2s</td>
                        <td className="col-speedup bar-78">38.8×</td>
                      </tr>
                      <tr>
                        <td className="col-query">Multi: Cardio+Neuro</td>
                        <td className="col-blockchain">98.3s</td>
                        <td className="col-hybrid">2.2s</td>
                        <td className="col-speedup bar-90">44.7×</td>
                      </tr>
                      <tr>
                        <td className="col-query">Multi: Cardio+Pulm+Onco</td>
                        <td className="col-blockchain">84.5s</td>
                        <td className="col-hybrid">1.7s</td>
                        <td className="col-speedup bar-100">50×</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="zk-perf-callout fade-in">
                  <div className="zk-perf-text">
                    <h3>Zero-Knowledge Proof Performance</h3>
                    <p>
                      RISC0 zkSTARK proofs provide sub-second verification times with post-quantum security.
                      Proof sizes range from 150–500 KB depending on complexity. While generation requires
                      minutes (accelerated with GPU), verification remains instant — enabling real-time
                      compliance assurance.
                    </p>
                  </div>
                  <div className="zk-perf-stats">
                    <div className="zk-stat">
                      <span className="zk-stat-value">&lt;1s</span>
                      <span className="zk-stat-label">to verify</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Tech Stack */}
          <section className="tech-stack-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">09</div>
                <h2>Technology Stack</h2>
              </div>

              <div className="tech-stack-grid">
                {techStack.map((category, idx) => (
                  <div key={idx} className={`tech-stack-category fade-in ${category.colorClass}`}>
                    <h3>{category.category}</h3>
                    <div className="tech-list">
                      {category.techs.map((tech, tidx) => (
                        <span key={tidx} className="tech-item">{tech}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Demo Deployment */}
          <section className="tech-stack-section deployment-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">10</div>
                <h2>Demo Deployment</h2>
              </div>
              <p className="section-intro fade-in">
                The live demo runs the full production stack, provisioned on-demand via AWS EC2 Spot Instances.
              </p>
              <div className="deployment-card fade-in">
                <div className="deployment-left">
                  <span className="deployment-panel-label">Provisioning</span>
                  <div className="deployment-steps">
                    <div className="deployment-step">
                      <div className="deploy-step-num">01</div>
                      <div className="deploy-step-body">
                        <h4>Request</h4>
                        <p>Clicking "Live Demo" triggers a Spot Instance request from the portfolio backend, which runs on a separate VPS.</p>
                      </div>
                    </div>
                    <div className="deployment-step">
                      <div className="deploy-step-num">02</div>
                      <div className="deploy-step-body">
                        <h4>Provision</h4>
                        <p>AWS provisions a fresh EC2 instance. The Kubernetes cluster boots, the Hyperledger Fabric network initialises, and all services come online.</p>
                      </div>
                    </div>
                    <div className="deployment-step">
                      <div className="deploy-step-num">03</div>
                      <div className="deploy-step-body">
                        <h4>Connect</h4>
                        <p>Once live, the portfolio embeds the running instance in an iframe. A countdown timer tracks the active session.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="deployment-divider" />
                <div className="deployment-right">
                  <span className="deployment-panel-label">Running Stack</span>
                  <div className="deploy-stack-list">
                    <div className="deploy-stack-item">
                      <div className="deploy-stack-icon deploy-icon-blue"><Database size={14} /></div>
                      <div className="deploy-stack-text">
                        <span className="deploy-stack-name">Hyperledger Fabric</span>
                        <span className="deploy-stack-sub">Kubernetes cluster via hlf-operator</span>
                      </div>
                    </div>
                    <div className="deploy-stack-item">
                      <div className="deploy-stack-icon deploy-icon-amber"><Server size={14} /></div>
                      <div className="deploy-stack-text">
                        <span className="deploy-stack-name">Sync Manager + EHR Server</span>
                        <span className="deploy-stack-sub">Node.js / Fastify REST APIs</span>
                      </div>
                    </div>
                    <div className="deploy-stack-item">
                      <div className="deploy-stack-icon deploy-icon-emerald"><Network size={14} /></div>
                      <div className="deploy-stack-text">
                        <span className="deploy-stack-name">Neo4j + PostgreSQL</span>
                        <span className="deploy-stack-sub">Standalone instances, pre-seeded</span>
                      </div>
                    </div>
                    <div className="deploy-stack-item">
                      <div className="deploy-stack-icon deploy-icon-violet"><Shield size={14} /></div>
                      <div className="deploy-stack-text">
                        <span className="deploy-stack-name">RISC0 zkVM</span>
                        <span className="deploy-stack-sub">ZK proof generation pipeline</span>
                      </div>
                    </div>
                  </div>
                  <div className="deploy-data-note">
                    <span className="deploy-data-tag">Synthetic data</span>
                    200 pre-seeded patients across cardiology, neurology, oncology, and pulmonology consent categories
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Research Impact */}
          <section className="impact-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">11</div>
                <h2>Research Impact</h2>
              </div>

              <div className="impact-content">
                <div className="impact-block impact-block-split fade-in">
                  <h3 className="impact-category">Academic</h3>
                  <p>
                    FaSTr V2 resolves the fundamental blockchain trust-performance dilemma in healthcare systems.
                    The BRIDGE protocol demonstrates that cryptographic guarantees and high-performance queries
                    are not mutually exclusive — achieving both through intelligent architectural separation.
                  </p>
                </div>

                <div className="impact-block impact-block-quote fade-in">
                  <h3>Clinical Research Enablement</h3>
                  <p>
                    Real-time cohort identification transforms clinical trial design. Researchers can explore
                    "what-if" scenarios interactively: "How many patients consented to cardiovascular research
                    AND pulmonary data sharing?" Quick responses enable iterative refinement impossible
                    with traditional blockchain approaches.
                  </p>
                </div>

                <div className="impact-block impact-block-split fade-in">
                  <h3 className="impact-category">Regulatory</h3>
                  <p>
                    Automated compliance verification through zero-knowledge proofs shifts compliance from periodic
                    manual audits to continuous cryptographic assurance. Data controllers gain real-time visibility
                    into processor compliance, while processors prove adherence without exposing proprietary methods.
                  </p>
                </div>

                <div className="impact-block impact-block-quote fade-in">
                  <h3>Production Viability</h3>
                  <p>
                    Validated to 10M patients with sub-15s queries; cloud-native Kubernetes deployment using
                    hlf-operator demonstrates production feasibility. AWS EC2 infrastructure supports elastic
                    scaling for hospital network deployments.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Future Directions */}
          <section className="future-work-section">
            <div className="container">
              <div className="section-header fade-in">
                <div className="section-number">12</div>
                <h2>Future Directions</h2>
              </div>

              <div className="future-thread fade-in">
                <div className="future-item">
                  <span className="future-number">01</span>
                  <div className="future-item-body">
                    <h3>Multi-Jurisdictional Compliance</h3>
                    <p>
                      Encoding requirements from multiple regulatory frameworks (HIPAA, CCPA, GDPR) to enable
                      international data sharing with automated compliance verification across jurisdictions.
                    </p>
                  </div>
                </div>
                <div className="future-item">
                  <span className="future-number">02</span>
                  <div className="future-item-body">
                    <h3>Cross-Organization Federation</h3>
                    <p>
                      Consent propagation protocols for hospital networks, enabling patients to grant consent once
                      while maintaining cryptographic verification across organizational boundaries.
                    </p>
                  </div>
                </div>
                <div className="future-item">
                  <span className="future-number">03</span>
                  <div className="future-item-body">
                    <h3>Federated Learning Integration</h3>
                    <p>
                      Combining fine-grained consent with secure multi-party computation for collaborative machine
                      learning that respects patient-level permissions without centralizing data.
                    </p>
                  </div>
                </div>
                <div className="future-item">
                  <span className="future-number">04</span>
                  <div className="future-item-body">
                    <h3>Performance Optimization</h3>
                    <p>
                      Automated schema migration, enhanced caching layers, and column-store databases for analytical
                      workloads to further improve query performance at massive scale.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Publication */}
          <section className="publication-section">
            <div className="container">
              <div className="publication-card fade-in">
                <p className="pub-overline">ACCEPTED AT</p>
                <div className="pub-venue-badge">
                  <FileText size={16} />
                  IEEE COMPSAC 2026
                </div>
                <h2>
                  Scalable Blockchain-based Healthcare Consent Management with Automated Compliance Verification
                </h2>
                <p className="pub-byline">
                  Suraj Gupta · Frederic Lang · Umar Ozeer · Gwen Salaün
                </p>
                <blockquote className="pub-abstract">
                  We present a novel protocol that maintains blockchain's cryptographic guarantees while delegating
                  queries to high-performance lookup tables, enabling fast category lookups across millions of patients.
                  Our system combines hierarchical consent trees, blockchain audit trails, and event-driven synchronization
                  with reconciliation mechanisms. Additionally, we implement automated GDPR compliance verification through
                  zero-knowledge proofs.
                </blockquote>
                <div className="pub-actions">
                  <a
                    className="paper-button"
                    href="https://hal.science/hal-05730325v1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileText size={18} />
                    Read the Paper
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Back to Top */}
          <div className="container">
            <button className="back-to-top fade-in" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Back to Top ↑
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default FaStrV2;
